// TSL Status Hook
// Provides comprehensive real-time TSL status information for positions

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Position } from '../types/mexc';
import { TrailingStopLossPersistenceService } from '../services/trailingStopLossPersistence';
import { TrailingStopLossEngine } from '../services/trailingStopLossEngine';
import { TSLActivityService } from '../services/tslActivityService';
import { useCurrentPrices } from './useCurrentPrices';

export interface TSLStatusInfo {
  // Basic status
  isEnabled: boolean;
  isActive: boolean;
  isPaused: boolean;

  // Configuration summary
  trailingDistance: number;
  trailingType: 'percentage' | 'fixed';
  activationThreshold?: number;

  // Current state
  currentTrailingStopPrice?: number;
  lastAdjustmentTime?: number;
  lastAdjustmentReason?: string;

  // Health indicators
  healthStatus: 'healthy' | 'warning' | 'error' | 'inactive';
  healthMessage?: string;

  // Performance metrics
  totalAdjustments: number;
  lastProcessingTime?: number;
  recentErrors: number;

  // Market context
  currentPrice?: number;
  priceSource?: 'polling' | 'api' | 'fallback';
  profitLocked?: number;
  profitPercentage?: number;
}

export interface TSLGlobalStatus {
  totalActivePositions: number;
  totalEnabledPositions: number;
  recentAdjustments: number;
  recentErrors: number;
  engineStatus: {
    isRunning: boolean;
    lastProcessTime?: number;
    nextProcessTime?: number;
  };
  performanceMetrics: {
    averageProcessingTime?: number;
    lastActivityTime?: number;
  };
}

export function useTSLStatus(position?: Position) {
  const [tslStatus, setTslStatus] = useState<TSLStatusInfo | null>(null);
  const [lastUpdate, setLastUpdate] = useState<number>(0);
  const { getCurrentPrice } = useCurrentPrices();

  // Get TSL activity service instance
  const activityService = useMemo(() => TSLActivityService.getInstance(), []);

  // Calculate TSL status for a position
  const calculateTSLStatus = useCallback((pos: Position): TSLStatusInfo => {
    const config = TrailingStopLossPersistenceService.getTSLConfig(pos.positionId);
    const currentPriceData = getCurrentPrice(pos.symbol);
    const currentPrice = currentPriceData?.price;

    // Get recent activity for this position
    const recentEntries = activityService.getFilteredEntries({
      positionId: pos.positionId,
      dateFrom: Date.now() - (24 * 60 * 60 * 1000) // Last 24 hours
    });

    const adjustmentEntries = recentEntries.filter(e => e.eventType === 'update' && e.success);
    const errorEntries = recentEntries.filter(e => !e.success);
    const lastAdjustment = adjustmentEntries[0]; // Most recent first

    if (!config) {
      return {
        isEnabled: false,
        isActive: false,
        isPaused: false,
        trailingDistance: 0,
        trailingType: 'percentage',
        healthStatus: 'inactive',
        healthMessage: 'TSL not configured',
        totalAdjustments: 0,
        recentErrors: 0,
        currentPrice,
        priceSource: currentPriceData?.source,
      };
    }

    // Determine health status
    let healthStatus: TSLStatusInfo['healthStatus'] = 'healthy';
    let healthMessage: string | undefined;

    if (!config.enabled) {
      healthStatus = 'inactive';
      healthMessage = 'TSL disabled';
    } else if (!config.isActive) {
      healthStatus = 'warning';
      healthMessage = 'TSL enabled but not active';
    } else if (errorEntries.length > 0) {
      const recentErrors = errorEntries.filter(e =>
        e.timestamp > Date.now() - (60 * 60 * 1000) // Last hour
      ).length;

      if (recentErrors > 2) {
        healthStatus = 'error';
        healthMessage = `${recentErrors} recent errors`;
      } else if (recentErrors > 0) {
        healthStatus = 'warning';
        healthMessage = `${recentErrors} recent error(s)`;
      }
    } else if (!currentPrice) {
      healthStatus = 'warning';
      healthMessage = 'No current price data';
    }

    // Calculate profit locked
    let profitLocked: number | undefined;
    let profitPercentage: number | undefined;

    if (config.currentTrailingStopPrice && currentPrice) {
      const isLong = pos.positionType === 1;
      const entryPrice = pos.openAvgPrice;

      if (isLong) {
        profitLocked = (config.currentTrailingStopPrice - entryPrice) * pos.holdVol;
        profitPercentage = ((config.currentTrailingStopPrice - entryPrice) / entryPrice) * 100;
      } else {
        profitLocked = (entryPrice - config.currentTrailingStopPrice) * pos.holdVol;
        profitPercentage = ((entryPrice - config.currentTrailingStopPrice) / entryPrice) * 100;
      }
    }

    return {
      isEnabled: config.enabled,
      isActive: config.isActive,
      isPaused: config.enabled && !config.isActive,
      trailingDistance: config.trailingDistance,
      trailingType: config.trailingType,
      activationThreshold: config.minProfitThreshold,
      currentTrailingStopPrice: config.currentTrailingStopPrice,
      lastAdjustmentTime: lastAdjustment?.timestamp,
      lastAdjustmentReason: lastAdjustment?.reason,
      healthStatus,
      healthMessage,
      totalAdjustments: adjustmentEntries.length,
      lastProcessingTime: config.lastUpdateTime,
      recentErrors: errorEntries.length,
      currentPrice,
      priceSource: currentPriceData?.source,
      profitLocked,
      profitPercentage,
    };
  }, [getCurrentPrice, activityService]);

  // Update TSL status when position or dependencies change
  useEffect(() => {
    if (!position) {
      setTslStatus(null);
      return;
    }

    const updateStatus = () => {
      const status = calculateTSLStatus(position);
      setTslStatus(status);
      setLastUpdate(Date.now());
    };

    updateStatus();

    // Listen for TSL activity updates
    const handleActivityUpdate = () => {
      updateStatus();
    };

    activityService.addListener(handleActivityUpdate);

    // Update every 30 seconds to sync with TSL engine
    const interval = setInterval(updateStatus, 30000);

    return () => {
      activityService.removeListener(handleActivityUpdate);
      clearInterval(interval);
    };
  }, [position, calculateTSLStatus, activityService]);

  return {
    tslStatus,
    lastUpdate,
    refresh: () => {
      if (position) {
        const status = calculateTSLStatus(position);
        setTslStatus(status);
        setLastUpdate(Date.now());
      }
    }
  };
}

// Hook for global TSL status across all positions
export function useGlobalTSLStatus(positions: Position[]): TSLGlobalStatus {
  const [globalStatus, setGlobalStatus] = useState<TSLGlobalStatus>({
    totalActivePositions: 0,
    totalEnabledPositions: 0,
    recentAdjustments: 0,
    recentErrors: 0,
    engineStatus: { isRunning: false },
    performanceMetrics: {}
  });

  const activityService = useMemo(() => TSLActivityService.getInstance(), []);

  useEffect(() => {
    const updateGlobalStatus = () => {
      const tslState = TrailingStopLossPersistenceService.getTSLState();
      const engine = TrailingStopLossEngine.getInstance();
      const engineStatus = engine.getStatus();
      const stats = activityService.getStats();

      // Count active and enabled positions
      let totalActive = 0;
      let totalEnabled = 0;

      positions.forEach(pos => {
        const config = tslState.configs[pos.positionId];
        if (config?.enabled) {
          totalEnabled++;
          if (config.isActive) {
            totalActive++;
          }
        }
      });

      // Get recent activity (last hour)
      const recentEntries = activityService.getFilteredEntries({
        dateFrom: Date.now() - (60 * 60 * 1000)
      });

      const recentAdjustments = recentEntries.filter(e =>
        e.eventType === 'update' && e.success
      ).length;

      const recentErrors = recentEntries.filter(e => !e.success).length;

      setGlobalStatus({
        totalActivePositions: totalActive,
        totalEnabledPositions: totalEnabled,
        recentAdjustments,
        recentErrors,
        engineStatus: {
          isRunning: engineStatus.isRunning,
          lastProcessTime: engineStatus.lastProcessTime || undefined,
          nextProcessTime: engineStatus.nextProcessTime || undefined,
        },
        performanceMetrics: {
          averageProcessingTime: stats.averageProcessingTime,
          lastActivityTime: stats.lastActivity,
        }
      });
    };

    updateGlobalStatus();

    // Listen for activity updates
    const handleActivityUpdate = () => {
      updateGlobalStatus();
    };

    activityService.addListener(handleActivityUpdate);

    // Update every 30 seconds
    const interval = setInterval(updateGlobalStatus, 30000);

    return () => {
      activityService.removeListener(handleActivityUpdate);
      clearInterval(interval);
    };
  }, [positions, activityService]);

  return globalStatus;
}

// Hook for multiple positions TSL status
export function useMultiPositionTSLStatus(positions: Position[]) {
  const [statusMap, setStatusMap] = useState<Record<number, TSLStatusInfo>>({});
  const { getCurrentPrice } = useCurrentPrices();
  const activityService = useMemo(() => TSLActivityService.getInstance(), []);

  const calculateAllStatuses = useCallback(() => {
    const newStatusMap: Record<number, TSLStatusInfo> = {};

    positions.forEach(position => {
      const config = TrailingStopLossPersistenceService.getTSLConfig(position.positionId);
      const currentPriceData = getCurrentPrice(position.symbol);
      const currentPrice = currentPriceData?.price;

      // Get recent activity for this position
      const recentEntries = activityService.getFilteredEntries({
        positionId: position.positionId,
        dateFrom: Date.now() - (24 * 60 * 60 * 1000)
      });

      const adjustmentEntries = recentEntries.filter(e => e.eventType === 'update' && e.success);
      const errorEntries = recentEntries.filter(e => !e.success);
      const lastAdjustment = adjustmentEntries[0];

      if (!config) {
        newStatusMap[position.positionId] = {
          isEnabled: false,
          isActive: false,
          isPaused: false,
          trailingDistance: 0,
          trailingType: 'percentage',
          healthStatus: 'inactive',
          totalAdjustments: 0,
          recentErrors: 0,
          currentPrice,
          priceSource: currentPriceData?.source,
        };
        return;
      }

      // Determine health status
      let healthStatus: TSLStatusInfo['healthStatus'] = 'healthy';
      let healthMessage: string | undefined;

      if (!config.enabled) {
        healthStatus = 'inactive';
        healthMessage = 'TSL disabled';
      } else if (!config.isActive) {
        healthStatus = 'warning';
        healthMessage = 'TSL enabled but not active';
      } else if (errorEntries.length > 0) {
        const recentErrors = errorEntries.filter(e =>
          e.timestamp > Date.now() - (60 * 60 * 1000)
        ).length;

        if (recentErrors > 2) {
          healthStatus = 'error';
          healthMessage = `${recentErrors} recent errors`;
        } else if (recentErrors > 0) {
          healthStatus = 'warning';
          healthMessage = `${recentErrors} recent error(s)`;
        }
      }

      newStatusMap[position.positionId] = {
        isEnabled: config.enabled,
        isActive: config.isActive,
        isPaused: config.enabled && !config.isActive,
        trailingDistance: config.trailingDistance,
        trailingType: config.trailingType,
        activationThreshold: config.minProfitThreshold,
        currentTrailingStopPrice: config.currentTrailingStopPrice,
        lastAdjustmentTime: lastAdjustment?.timestamp,
        lastAdjustmentReason: lastAdjustment?.reason,
        healthStatus,
        healthMessage,
        totalAdjustments: adjustmentEntries.length,
        lastProcessingTime: config.lastUpdateTime,
        recentErrors: errorEntries.length,
        currentPrice,
        priceSource: currentPriceData?.source,
      };
    });

    setStatusMap(newStatusMap);
  }, [positions, getCurrentPrice, activityService]);

  useEffect(() => {
    calculateAllStatuses();

    // Listen for activity updates
    activityService.addListener(calculateAllStatuses);

    // Update every 30 seconds
    const interval = setInterval(calculateAllStatuses, 30000);

    return () => {
      activityService.removeListener(calculateAllStatuses);
      clearInterval(interval);
    };
  }, [calculateAllStatuses, activityService]);

  return statusMap;
}
